import { addDays, parseISO, format } from "date-fns";
import { supabase } from "@/lib/supabase";
import type { ReportResolver, ReportRow, Grain, ReportContext } from "@/lib/reports/types";

export interface BfProsjektRad {
  projectId: string;
  name: string;
  bfEst: number;
  bfReell: number;
}

/** Ren transform — enhetstestbar. avvik = bfEst ? round((bfReell-bfEst)/bfEst*100) : 0 */
export function bfRows(data: BfProsjektRad[]): ReportRow[] {
  return data.map((d) => ({
    id: d.projectId,
    label: d.name,
    canDrill: true,
    cells: {
      bfEst: d.bfEst,
      bfReell: d.bfReell,
      avvik: d.bfEst ? Math.round(((d.bfReell - d.bfEst) / d.bfEst) * 100) : 0,
    },
  }));
}

/**
 * Per-prosjekt estimat-vs-reell BF. `bf_estimated`/`bf_actual` er øyeblikksbilde-
 * tall synket fra PowerOffice (ikke periodisert per år).
 *
 * Scope:
 * - Standard: kun avsluttede (completed) prosjekter med `completed_at` i valgt
 *   periode (`ctx.period`). Velg «År» for å avstemme reell-summen mot dashboardets
 *   YTD (samme `completed_at`-vindu som `calcBfYearTotals` bøtter reell på).
 * - Med `filters.includeActive`: ta også med aktive/kommende prosjekter (løpende,
 *   så de filtreres IKKE på dato — `bf_actual` er da et delvis øyeblikksbilde).
 * - Kansellerte, soft-slettede og interne (ingen BF, jf. bf-buckets.ts) ekskluderes
 *   alltid.
 */
async function fetchBfProsjekt(ctx: ReportContext): Promise<BfProsjektRad[]> {
  const from = ctx.period.from;
  // Eksklusiv øvre grense = dagen etter `to`, så hele sluttdagen teller med.
  const toExclusive = format(addDays(parseISO(ctx.period.to), 1), "yyyy-MM-dd");
  const includeActive = ctx.filters.includeActive ?? false;

  const { data, error } = await supabase
    .from("projects")
    .select("id, name, bf_estimated, bf_actual, completed_at, project_status, is_internal")
    .is("deleted_at", null)
    .neq("project_status", "cancelled");
  if (error) throw error;

  return (data ?? [])
    .filter((p) => {
      if (p.is_internal) return false;
      if (p.project_status === "completed") {
        const c = p.completed_at as string | null;
        // ISO-strenger sammenlignes leksikografisk == kronologisk.
        return c != null && c >= from && c < toExclusive;
      }
      // Pågående/kommende er løpende — kun med når includeActive, uten datofilter.
      return includeActive && (p.project_status === "active" || p.project_status === "kommende");
    })
    .map((p) => ({
      projectId: p.id as string,
      name: (p.name as string) ?? "",
      bfEst: Number(p.bf_estimated ?? 0),
      bfReell: Number(p.bf_actual ?? 0),
    }));
}

export const bfResolver: ReportResolver = {
  async fetch(level: Grain, ctx: ReportContext): Promise<ReportRow[]> {
    if (level === "prosjekt") return bfRows(await fetchBfProsjekt(ctx));
    // budsjettlinje-drill kan legges til senere
    return [];
  },
};
